import { InstrumentModel } from '@javascript/habitual-analytics/models/instrument/types';
import { TransactionTypes } from '@javascript/habitual-analytics/domains';
import { TradingSymbol } from '@javascript/habitual-analytics/brokers/OrderFacade/types/TradingSymbol';
import {
  Exchanges, OrderTypes,
  OrderStatus, ProductTypes,
  BrokerOrderId, SuccessResult,
  MarginCalculation
} from '@javascript/habitual-analytics/brokers/OrderFacade/types/common';

export type TableField = { label: string, key: string };
export type BrokerConfigs = {
  placeOrderLabelConfigs: {
    mis: string;
    nrml: string;
    cnc: string;
  };
  booksLabelConfigs: {
    mis: string;
    nrml: string;
    cnc: string;
  };
  tabsLabelConfigs: {
    orderBook: string;
    tradeBook: string;
    positionBook: string;
    holdingBook: string;
    basketOrders: string;
  };
  orderTableHeaders: TableField[];
  tradeTableHeaders: TableField[];
  positionTableHeaders: TableField[];
  holdingTableHeaders: TableField[];
};

export type TargetRestEndPoints = {
  placeOrders: string;
  cancelOrders: string;
  modifyOrders: string;
  orderBook: string;
  tradeBook: string;
  positionBook: string;
  holdingBook: string;
  marginCalculation: string;
  postBackOrder: string;
};

export namespace Insta {

  export type PlaceOrderResult = BrokerOrderId;
  export type ModifyOrderResult = BrokerOrderId;
  export type CancelOrderResult = { orderId: BrokerOrderId };
  export type MarginCalculationResult = SuccessResult<MarginCalculation>
    | { success: false, message: string };

  export type OrderBookResult = SuccessResult<{
    headers: TableField[], orderBookDetails: OrderBook[]
  }> | { success: false, message: string, headers: TableField[], orderBookDetails: [] };

  export type TradeBookResult = SuccessResult<{
    headers: TableField[], tradeBookDetails: TradeBook[]
  }> | { success: false, message: string, headers: TableField[], tradeBookDetails: [] };

  export type PositionBookResult = SuccessResult<{
    headers: TableField[], positionBookDetails: PositionBook[]
  }> | { success: false, message: string, headers: TableField[], positionBookDetails: [] };

  export type HoldingBookResult = SuccessResult<{
    headers: TableField[], holdingBookDetails: HoldingBook[]
  }> | { success: false, message: string, headers: TableField[], holdingBookDetails: [] };

  export interface OrderBook {
    tradingSymbolObj: InstrumentModel;
    type: TransactionTypes;
    time: string;
    status: InstaStatus;
    isCancellableOrder: boolean;
    stopPrice: number;
    limitPrice: number;
    failedReason: string;
    extraDetails: {
      product: string;
      qty: string;
      orderNo: string;
      tradedPrice: number;
      defaultProductType: string;
      defaultProductCode: ProductTypes;
    };
  };

  export interface TradeBook {
    tradingSymbolObj: InstrumentModel;
    time: string;
    type: TransactionTypes;
    status: InstaStatus;
    quantity: number;
    extraDetails: {
      product: string;
      qty: string;
      tradedPrice: number;
    };
  };
  export interface PositionBook {
    tradingSymbolObj: InstrumentModel;
    type: TransactionTypes;
    qty: number;
    buyAvg: number;
    sellAvg: number;
    ltp: number;
    buyQuantity: number;
    sellQuantity: number;
    avgPrice: number;
    profitLoss: number;
    realizedProfitLoss: number;
    extraDetails: {
      product: string;
      isOpenPosition: boolean;
      type: TransactionTypes | string;
      defaultProductCode: ProductTypes;
    };
  };

  export interface HoldingBook {
    tradingSymbolObj: InstrumentModel;
    ltp: number;
    profitLoss: number;
    extraDetails: {
      quantity: number;
      buyAverage: number;
      sellAverage: number;
      buyValue: number;
      averagePrice: number;
      netChg: number;
    };
  };

  export interface Postback {
    tradingSymbolObj: InstrumentModel;
    status: InstaStatus;
    failedReason: string;
    extraDetails: {
      orderNo: string;
    };
  };

  export interface PlaceOrderDetail {
    tradingSymbolObj: InstrumentModel;
    transactionType: TransactionTypes;
    ltp: number;
    actualPrice: number;
    userId: string;
    exch: string;
    pCode: ProductTypes;
    price: number;
    qty: number;
    prctyp: DefaultOrderType;
    triggerPrice: number;
    complexty: string;
    type: string;
    disclosedQty: number;
    symbol: string;
    tradingSymbol: TradingSymbol;
    prevDayClose: number;
    status: string;
  };

  export interface CancelOrderDetail {
    orderNo: string;
    tradingSymbolObj: InstrumentModel;
    orderDetails: {
      tradindSymbolObj: TradingSymbol;
      type: TransactionTypes;
      status: OrderStatus;
      isCancellableOrder: boolean;
      stopPrice: number;
      limitPrice: number;
      failedReason: string;
      extraDetails: {
        product: string;
        qty: string;
        orderNo: string;
        tradedPrice: number;
        defaultProductType: DefaultOrderType;
        defaultProductCode: ProductTypes;
      };
      ltpConfig: {
        ltp: number;
        change: string;
        changePercentage: string;
      };
    };
  };

  export interface ModifyOrderDetail {
    userId: string;
    exch: string;
    pCode: ProductTypes;
    price: number;
    actualPrice: number;
    qty: number;
    transactionType: TransactionTypes;
    prctyp: DefaultOrderType;
    trigPrice: number;
    complexty: string;
    type: TransactionTypes;
    ret: string;
    disclosedQty: number;
    symbol: string;
    tradingSymbol: string;
    ltp: number;
    prevDayClose: number;
    tradingSymbolObj: InstrumentModel;
    orderNo: string;
    extraDetails: {
      product: string;
      qty: string;
      orderNo: string;
      tradedPrice: number;
      defaultProductType: string;
      defaultProductCode: ProductTypes;
    };
    status?: string;
  };

  export interface MarginCalculationDetail {
    tradingSymbolObj: InstrumentModel;
    transactionType: TransactionTypes;
    ltp: number;
    actualPrice: number;
    userId: string;
    exch: string;
    pCode: ProductTypes;
    price: number;
    qty: number;
    prctyp: DefaultOrderType;
    triggerPrice: number;
    complexty: string;
    type: string;
    disclosedQty: number;
    symbol: string;
    tradingSymbol: TradingSymbol;
    prevDayClose: number;
  };
  export interface PostBackOrderDetail {
    brokerOrderId: String;
    exchangeOrderId?: String;
    orderTimeStamp: Date;
    exchange: Exchanges;
    tradingSymbol: TradingSymbol;
    transactionType: TransactionTypes;
    orderType: OrderTypes;
    productType: ProductTypes;
    limitPrice: number;
    triggerPrice: number;
    quantity: number;
    filledQuantity: number;
    pendingQuantity: number;
    cancelledQuantity: number;
    tradedPrice: number;
    status: OrderStatus;
    lotSize?: number;
    message: String;
  };

}
/* eslint-disable @typescript-eslint/naming-convention */

export enum InstaStatus {
  CANCELLED = 'cancelled',
  TRADED = 'executed',
  FAILED = 'failed',
  PENDING = 'pending',
  EXPIRED = 'expired',
}

export enum DefaultOrderType {
  LIMIT = 'LIMIT',
  MARKET = 'MARKET',
  SL_M = 'STOP',
  SL_L = 'STOPLIMIT',
};

export enum LocalOrderType {
  LIMIT = 'L',
  MARKET = 'M',
  SL_M = 'SL-M',
  SL_L = 'SL',
};